import { getDb } from "@/services/storage/sqlite/connection";
import { createId } from "@/utils";
import type { Prompt, PromptCategory } from "@/types";
import { extractVariables } from "@/types";

interface PromptRow {
  id: string;
  workspace_id: string;
  category_id: string | null;
  title: string;
  body: string;
  variables_json: string;
  is_favorite: number;
  created_at: number;
  updated_at: number;
}

interface PromptCategoryRow {
  id: string;
  workspace_id: string;
  name: string;
  created_at: number;
}

function rowToPrompt(row: PromptRow): Prompt {
  return {
    id: row.id,
    workspaceId: row.workspace_id,
    categoryId: row.category_id,
    title: row.title,
    body: row.body,
    variables: JSON.parse(row.variables_json) as string[],
    isFavorite: row.is_favorite === 1,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

function rowToCategory(row: PromptCategoryRow): PromptCategory {
  return { id: row.id, workspaceId: row.workspace_id, name: row.name, createdAt: row.created_at };
}

export const promptRepository = {
  async list(workspaceId: string): Promise<Prompt[]> {
    const db = await getDb();
    const rows = await db.select<PromptRow[]>(
      "SELECT * FROM prompts WHERE workspace_id = $1 ORDER BY is_favorite DESC, updated_at DESC",
      [workspaceId],
    );
    return rows.map(rowToPrompt);
  },

  async create(input: { workspaceId: string; title: string; body: string; categoryId?: string | null }): Promise<Prompt> {
    const db = await getDb();
    const now = Date.now();
    const prompt: Prompt = {
      id: createId("prompt"),
      workspaceId: input.workspaceId,
      categoryId: input.categoryId ?? null,
      title: input.title,
      body: input.body,
      variables: extractVariables(input.body),
      isFavorite: false,
      createdAt: now,
      updatedAt: now,
    };
    await db.execute(
      `INSERT INTO prompts (id, workspace_id, category_id, title, body, variables_json, is_favorite, created_at, updated_at)
       VALUES ($1, $2, $3, $4, $5, $6, 0, $7, $8)`,
      [
        prompt.id,
        prompt.workspaceId,
        prompt.categoryId,
        prompt.title,
        prompt.body,
        JSON.stringify(prompt.variables),
        now,
        now,
      ],
    );
    return prompt;
  },

  async update(id: string, patch: { title?: string; body?: string; categoryId?: string | null }): Promise<void> {
    const db = await getDb();
    const rows = await db.select<PromptRow[]>("SELECT * FROM prompts WHERE id = $1", [id]);
    const existing = rows[0];
    if (!existing) return;
    const title = patch.title ?? existing.title;
    const body = patch.body ?? existing.body;
    const categoryId = patch.categoryId !== undefined ? patch.categoryId : existing.category_id;
    await db.execute(
      "UPDATE prompts SET title = $1, body = $2, category_id = $3, variables_json = $4, updated_at = $5 WHERE id = $6",
      [title, body, categoryId, JSON.stringify(extractVariables(body)), Date.now(), id],
    );
  },

  async setFavorite(id: string, isFavorite: boolean): Promise<void> {
    const db = await getDb();
    await db.execute("UPDATE prompts SET is_favorite = $1, updated_at = $2 WHERE id = $3", [
      isFavorite ? 1 : 0,
      Date.now(),
      id,
    ]);
  },

  async delete(id: string): Promise<void> {
    const db = await getDb();
    await db.execute("DELETE FROM prompts WHERE id = $1", [id]);
  },

  // --- Categories ----------------------------------------------------------

  async listCategories(workspaceId: string): Promise<PromptCategory[]> {
    const db = await getDb();
    const rows = await db.select<PromptCategoryRow[]>(
      "SELECT * FROM prompt_categories WHERE workspace_id = $1 ORDER BY name ASC",
      [workspaceId],
    );
    return rows.map(rowToCategory);
  },

  async createCategory(workspaceId: string, name: string): Promise<PromptCategory> {
    const db = await getDb();
    const category: PromptCategory = { id: createId("pcat"), workspaceId, name, createdAt: Date.now() };
    await db.execute("INSERT INTO prompt_categories (id, workspace_id, name, created_at) VALUES ($1, $2, $3, $4)", [
      category.id,
      category.workspaceId,
      category.name,
      category.createdAt,
    ]);
    return category;
  },

  async deleteCategory(id: string): Promise<void> {
    const db = await getDb();
    await db.execute("DELETE FROM prompt_categories WHERE id = $1", [id]);
  },
};
